// Combined multi-category quote engine. A customer can select any combination of the 4
// categories below in one quote/booking — this computes one blended total across all of them,
// including the per-line carpet discount and the order-level volume discount.
// Kept in sync by hand with backend/src/services/pricingCalculator.js.

export const MINIMUM_BOOKING = 90;

// Order-level discount applies once, to the whole post-line-discount subtotal — not marginal
// brackets. Highest threshold met wins.
export const ORDER_DISCOUNT_TIERS = [
  { threshold: 500, rate: 0.15 },
  { threshold: 420, rate: 0.1 },
  { threshold: 350, rate: 0.085 },
  { threshold: 250, rate: 0.075 },
  { threshold: 181, rate: 0.05 },
];

// Carpet rooms are tiered, not a flat per-room rate: 1-2 rooms at $45/room, a $120 flat package
// at 3 rooms, then $45/room for every room beyond 3.
export const carpetRoomsTotal = (rooms) => {
  if (rooms <= 0) return 0;
  if (rooms === 1) return 45;
  if (rooms === 2) return 90;
  if (rooms === 3) return 120;
  return 120 + (rooms - 3) * 45;
};

export const CATEGORIES = [
  {
    key: "carpet",
    label: "Carpet and Rug",
    fields: [
      {
        key: "rooms",
        label: "Carpet cleaning — Room",
        question: "How many rooms of carpet would you like cleaned?",
        helper: "Standard rate for carpet cleaning is $120 for 3 rooms up to 15m² each. An additional fee applies for any room exceeding 15m².",
        unitPrice: 45,
        tiered: true,
      },
      {
        key: "rugs",
        label: "Rug cleaning",
        question: "How many rugs would you like cleaned?",
        helper: "$45 per rug up to 6m² each. An additional fee applies for any rug exceeding 6m².",
        unitPrice: 45,
      },
    ],
  },
  {
    key: "upholstery",
    label: "Upholstery and Couch",
    fields: [
      {
        key: "couches",
        label: "Couch / seat cleaning",
        question: "How many couch seats would you like cleaned?",
        helper: "Count total individual seat cushions (e.g., a 3-seater sofa = 3 seats)",
        unitPrice: 40,
      },
    ],
  },
  {
    key: "mattress",
    label: "Mattress Cleaning",
    fields: [
      {
        key: "singleMattresses",
        label: "Mattress cleaning — Single",
        question: "How many single mattresses would you like cleaned?",
        helper: "$120.00 each",
        unitPrice: 120,
      },
      {
        key: "doubleQueenKingMattresses",
        label: "Mattress cleaning — Double/Queen/King",
        question: "How many double, queen, or king mattresses would you like cleaned?",
        helper: "$180.00 each",
        unitPrice: 180,
      },
    ],
  },
  {
    key: "curtains",
    label: "Curtains and Blinds",
    fields: [
      {
        key: "windowCurtains",
        label: "Curtain cleaning — Window",
        question: "How many window curtain sets would you like cleaned?",
        helper: "$80.00 per set",
        unitPrice: 80,
      },
      {
        key: "doorCurtains",
        label: "Curtain cleaning — Door",
        question: "How many door or full-length glass door curtain sets need cleaning?",
        helper: "$100.00 per set",
        unitPrice: 100,
      },
    ],
  },
  {
    key: "window",
    label: "Windows Cleaning (Glass)",
    fields: [
      {
        key: "windowGlass",
        label: "Glass cleaning — Window",
        question: "How many window glass panes would you like cleaned?",
        helper: "$25.00 per pane (includes inside and outside cleaning)",
        unitPrice: 25,
      },
      {
        key: "doorGlass",
        label: "Glass cleaning — Door",
        question: "How many glass door panes (e.g., sliding patio doors) need cleaning?",
        helper: "$40.00 per pane (includes inside and outside cleaning)",
        unitPrice: 40,
      },
    ],
  },
];

const round2 = (value) => Math.round(value * 100) / 100;

const getOrderDiscountRate = (subtotal) => {
  const tier = ORDER_DISCOUNT_TIERS.find((t) => subtotal >= t.threshold);
  return tier ? tier.rate : 0;
};

// selectedDetails: { [categoryKey]: { [fieldKey]: quantity } } — only selected categories should
// be present; categories/fields omitted or at 0 simply contribute no line items.
export const calculateQuote = (selectedDetails = {}) => {
  const lineItems = [];

  CATEGORIES.forEach((category) => {
    const categoryDetails = selectedDetails[category.key];
    if (!categoryDetails) return;

    category.fields.forEach((field) => {
      const quantity = Number(categoryDetails[field.key]) || 0;
      if (quantity <= 0) return;

      const standardTotal = quantity * field.unitPrice;
      const amount = field.tiered ? carpetRoomsTotal(quantity) : standardTotal;
      const lineDiscount = round2(standardTotal - amount);

      lineItems.push({
        category: category.key,
        field: field.key,
        label: field.label,
        quantity,
        unitPrice: field.unitPrice,
        standardTotal: round2(standardTotal),
        lineDiscount,
        amount: round2(amount),
      });
    });
  });

  const serviceSubtotal = round2(lineItems.reduce((sum, item) => sum + item.standardTotal, 0));
  const quantityDiscounts = round2(lineItems.reduce((sum, item) => sum + item.lineDiscount, 0));
  const subtotal = round2(serviceSubtotal - quantityDiscounts);
  const orderDiscountRate = getOrderDiscountRate(subtotal);
  const orderDiscount = round2(subtotal * orderDiscountRate);
  const total = round2(subtotal - orderDiscount);

  return {
    lineItems,
    serviceSubtotal,
    quantityDiscounts,
    subtotal,
    orderDiscountRate,
    orderDiscount,
    total,
    meetsMinimum: total >= MINIMUM_BOOKING,
  };
};
